<script lang="ts">
    import { onMount } from 'svelte/internal'
    import Fa from 'svelte-fa'
    import { faEdit, faPlus, faRemove } from '@fortawesome/free-solid-svg-icons'
    import { AudiencesRepository } from '../../modules/eventsApi/audiences/audiences.repository'
    import { AudiencesService } from '../../modules/eventsApi/audiences/audiences.service'
    import type { Audience } from '../../modules/eventsApi/audiences/audience'
    import type { TagEntity } from '../../modules/eventsApi/tags/tag.entity'
    import type { Person } from '../../modules/eventsApi/persons/person'
    import dayjs from 'dayjs'
    import Zframe from '../_common/z/zframe.svelte'

    // ##############################################################################################################

    export let personId: string

    const rep = new AudiencesRepository()
    const service = new AudiencesService(rep)
    let dynamicAudiences: Audience[] = []
    let staticAudiences: Audience[] = []
    let personAudiences: Audience[] = []
    let haveDynamic = false
    let show = false

    let tags: TagEntity[] = []
    let person: Person = null

    async function loadEntities() {
        try {
            show = true
            let audiences: Audience[] = []
            audiences = await service.getAll()
            dynamicAudiences = audiences.filter((el) => el.type === 'dynamic')
            staticAudiences = audiences.filter((el) => el.type === 'static')
            personAudiences = await service.getAll({ personId })
            haveDynamic = !!dynamicAudiences.find((el) =>
                personAudiences.map((el) => el.audienceId).includes(el.audienceId),
            )

            tags = await window.api.events.tags.getAll()
            person = await window.api.events.persons.getOne(personId, { tags: true })
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    function wasAdded(audienceId: string) {
        const found = personAudiences.find((el) => el.audienceId === audienceId)
        console.log('found', found)
        return !!found
    }

    function wasAddedTag(tagId: string) {
        const found = person.tag.find((el) => el.tagId === tagId)
        console.log('found', found)
        return !!found
    }

    function evaluateError(error) {
        window.error(error)
    }

    async function toggleToAudience(audienceId: string) {
        if (wasAdded(audienceId)) {
            await service.removeFromAudience(audienceId, personId)
        } else {
            await service.addPersonToAudience(audienceId, personId)
        }
        await loadEntities()
    }

    async function toggleTag(tagId: string) {
        if (wasAddedTag(tagId)) {
            await window.api.events.tags.removePerson(tagId, personId)
        } else {
            await window.api.events.tags.addPerson(tagId, personId)
        }
        await loadEntities()
    }

    onMount(async () => {
        await loadEntities()
    })
</script>

{#if !show}
    <Zframe
        title="Públicos Estáticos"
        headline="Públicos estáticos que contêm esta pessoa. Você pode adicionar e remover uma pessoa a um ou mais públicos manualmente."
    >
        <div class="grid-head p-2">
            <div class="row green">
                <div class="col"></div>
                <div class="col-md-2">Adicionar</div>
            </div>
        </div>
        <div class="grid-body p-2">
            {#each staticAudiences as audience}
                <div class="row line liner py-1">
                    <div class="col">{audience?.title}</div>
                    <div class="col-md-2">
                        <div class="spacer" />
                        <div class="form-check form-switch" title="Adicionar ou remover a pessoa no público">
                            <input
                                class="form-check-input"
                                checked={wasAdded(audience.audienceId)}
                                on:change={() => {
                                    toggleToAudience(audience.audienceId)
                                }}
                                type="checkbox"
                                role="switch"
                                id="staticAud"
                            />
                        </div>
                    </div>
                </div>
            {:else}
                <div class="row line text-muted"><div class="col">Não há públicos estáticos criados</div></div>
            {/each}
        </div>
    </Zframe>
    <Zframe
        class="mt-3"
        title="Públicos Dinâmicos"
        headline="Públicos dinâmicos que contêm esta pessoa. A pessoa é adicionada ou removida a um público através de regras."
    >
        <div class="grid-body p-2">
            {#if haveDynamic}
                {#each dynamicAudiences as audience}
                    {#if wasAdded(audience.audienceId)}
                        <div class="row line liner py-1">
                            <div class="col">{audience?.title}</div>
                        </div>
                    {/if}
                {/each}
            {:else}
                <div class="row line text-muted">
                    <div class="col">Esta pessoa não está em um público dinâmico</div>
                </div>
            {/if}
        </div>
    </Zframe>
    {#if window.beta}
        <Zframe
            class="mt-3"
            title="Etiquetas"
            headline="Etiquetas atribuídas a esta pessoa. A pessoa é associada ou desassociada a uma ou mais etiquetas manualmente ou através de regras."
        >
            <div class="grid-head p-2">
                <div class="row green">
                    <div class="col">Etiqueta</div>
                    <div class="col">Primeira atribuição</div>
                    <div class="col">Última atribuição</div>
                    <div class="col-md-2">Adicionar</div>
                </div>
            </div>
            <div class="grid-body p-2">
                {#each tags as tag}
                    {@const ptag = person.tag.find((el) => el.tagId === tag.tagId)}
                    <div class="row line liner py-1">
                        <div class="col">{tag?.label}</div>
                        <div class="col">
                            {ptag?.firstEventAt ? dayjs(ptag?.firstEventAt).format('DD/MM/YYYY') : '-'}
                        </div>
                        <div class="col">{ptag?.lastEventAt ? dayjs(ptag?.lastEventAt).format('DD/MM/YYYY') : '-'}</div>
                        <div class="col-md-2">
                            <div class="spacer" />
                            <div class="form-check form-switch" title="Adicionar ou remover esta etiqueta">
                                <input
                                    class="form-check-input"
                                    checked={wasAddedTag(tag.tagId)}
                                    on:change={() => {
                                        toggleTag(tag.tagId)
                                    }}
                                    type="checkbox"
                                    role="switch"
                                    id="tag"
                                />
                            </div>
                        </div>
                    </div>
                {:else}
                    <div class="row line text-muted"><div class="col">Não há etiquetas criadas</div></div>
                {/each}
            </div>
        </Zframe>
    {/if}
{/if}

<style>
    .line {
        font-size: 14px;
    }
    .green {
        color: #379e75;
    }
    .form-check {
        display: flex;
        align-items: center;
    }
</style>
